/**
 * Feature flags and kill switches for graceful degradation
 */

// Feature flags with environment variable support
const getEnvFlag = (key, defaultValue = false) => {
  if (typeof process !== 'undefined' && process.env) {
    const value = process.env[key];
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  
  if (typeof window !== 'undefined' && window.import && window.import.meta && window.import.meta.env) {
    const value = window.import.meta.env[key];
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  
  return defaultValue;
};

export const FEATURE_FLAGS = {
  // Auto-save system
  FEATURE_AUTO_SAVE: getEnvFlag('REACT_APP_FEATURE_AUTO_SAVE', true),
  
  // Session monitoring
  FEATURE_SESSION_MONITOR: getEnvFlag('REACT_APP_FEATURE_SESSION_MONITOR', true),
  
  // IndexedDB backup
  FEATURE_INDEXEDDB_BACKUP: getEnvFlag('REACT_APP_FEATURE_INDEXEDDB_BACKUP', true),
  
  // Service worker
  FEATURE_SERVICE_WORKER: getEnvFlag('REACT_APP_FEATURE_SERVICE_WORKER', false),
  
  // Advanced error reporting
  FEATURE_ERROR_REPORTING: getEnvFlag('REACT_APP_FEATURE_ERROR_REPORTING', true),
  
  // Debug logging
  FEATURE_DEBUG_LOGGING: getEnvFlag('REACT_APP_FEATURE_DEBUG_LOGGING', false),
  
  // Finalize Order workflow (centralized status changes + enhanced activity logging)
  FEATURE_FINALIZE_ORDER: getEnvFlag('REACT_APP_FEATURE_FINALIZE_ORDER', true) // Enabled by default for staging
};

// Runtime feature toggles
class FeatureManager {
  constructor() {
    this.features = { ...FEATURE_FLAGS };
    this.disabledReasons = new Map();
  }
  
  isEnabled(featureName) {
    return this.features[featureName] === true;
  }
  
  disable(featureName, reason = 'manual_disable') {
    if (this.features[featureName]) {
      this.features[featureName] = false;
      this.disabledReasons.set(featureName, reason);
      console.warn(`Feature ${featureName} disabled: ${reason}`);
    }
  }
  
  enable(featureName) {
    this.features[featureName] = true;
    this.disabledReasons.delete(featureName);
    console.log(`Feature ${featureName} enabled`);
  }
  
  getDisabledReason(featureName) {
    return this.disabledReasons.get(featureName) || null;
  }
  
  getStatus() {
    return {
      features: { ...this.features },
      disabled: Object.fromEntries(this.disabledReasons)
    };
  }
}

// Global feature manager instance
export const featureManager = new FeatureManager();

// Helper function to check if a feature should be used
export function useFeature(featureName, fallbackBehavior = null) {
  const enabled = featureManager.isEnabled(featureName);
  
  if (!enabled && fallbackBehavior) {
    const reason = featureManager.getDisabledReason(featureName);
    console.warn(`Feature ${featureName} disabled (${reason}), using fallback`);
    return fallbackBehavior;
  }
  
  return enabled;
}

// Auto-disable features when dependencies fail
export function autoDisableOnError(featureName, error, context = '') {
  const reason = `auto_disabled_error: ${error.message || error} ${context}`.trim();
  featureManager.disable(featureName, reason);
}

// Service availability checker
// NOTE: Only returns false (unavailable) for 404. 
// For 502/503/504, returns true to allow retry - these are temporary issues.
export async function checkServiceAvailability(apiUrl, endpoint) {
  try {
    const response = await fetch(`${apiUrl}${endpoint}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    // 404 means endpoint doesn't exist - disable feature
    if (response.status === 404) {
      console.warn(`Service endpoint not found: ${endpoint}`);
      return false;
    }
    
    // 502/503/504 are temporary - don't disable, assume available
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      console.warn(`Service temporarily unavailable (${response.status}) for ${endpoint}, assuming available`);
      return true; // Don't disable on temporary errors
    }
    
    return response.ok;
  } catch (error) {
    // Network errors are temporary - assume service is available
    console.warn(`Service check network error for ${endpoint}:`, error.message);
    return true; // Don't disable on network errors
  }
}

// Initialize feature checks
export async function initializeFeatureChecks(apiUrl) {
  // Check session endpoint availability
  if (featureManager.isEnabled('FEATURE_SESSION_MONITOR')) {
    const sessionAvailable = await checkServiceAvailability(apiUrl, '/session/ttl');
    if (!sessionAvailable) {
      featureManager.disable('FEATURE_SESSION_MONITOR', 'session_endpoint_unavailable');
    }
  }
  
  // Log feature status
  if (featureManager.isEnabled('FEATURE_DEBUG_LOGGING')) {
    console.log('Feature initialization complete:', featureManager.getStatus());
  }
}